import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Lifebuoy } from "@phosphor-icons/react";
import { money } from "../lib/pricing";

const Stat = ({ label, value, sub, tone = "", testid }) => (
  <div className="rounded-md border border-border px-3 py-2" data-testid={testid}>
    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className={`text-sm font-bold font-[Chivo] ${tone}`}>{value}</p>
    {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
  </div>
);

const TrendTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-card p-2.5 text-[11px] shadow-md" data-testid="recovery-trend-tooltip">
      <p className="font-bold mb-1">w/c {d.label}{d.current ? " (this week)" : ""}</p>
      <p className="text-amber-600">At risk: {money(d.at_risk)}/mo · {d.at_risk_count} workspace{d.at_risk_count === 1 ? "" : "s"}</p>
      <p className="text-emerald-600">Recovered: {money(d.recovered)}/mo · {d.recovered_count}</p>
      <p className="text-muted-foreground">New failures: {money(d.new_failed)}/mo · {d.new_failed_count}</p>
      <p className="text-muted-foreground">Trials converted: {d.trials_converted} · Chases: {d.chases} · Win-backs: {d.winbacks}</p>
    </div>
  );
};

/** Weekly MRR at risk (past-due) vs MRR recovered — dunning health over the last 12 weeks. */
export default function RecoveryTrendChart({ weeks = 12 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { API.get("/platform/payment-recovery/trend", { params: { weeks } }).then(({ data: d }) => setData(d)).catch(() => setError(true)); }, [weeks]);

  const t = data?.totals;
  return (
    <div className="border border-border rounded-lg bg-card p-4 mb-6" data-testid="recovery-trend-chart">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="text-sm font-bold font-[Chivo] flex items-center gap-1.5"><Lifebuoy size={14} className="text-muted-foreground" /> Payment Recovery — last {weeks} weeks</h3>
          <p className="text-[10px] text-muted-foreground">MRR sitting in past-due workspaces at each week end vs MRR recovered that week</p>
        </div>
        <Link to="/platform/subscription-plans#payment-recovery" className="text-xs text-primary hover:underline" data-testid="recovery-trend-open-panel">Open payment recovery →</Link>
      </div>
      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3" data-testid="recovery-trend-stats">
          <Stat label="At risk now" value={`${money(t.at_risk_now, { compact: true })}/mo`} sub={`${t.at_risk_count_now} past due`} tone="text-amber-600" testid="recovery-stat-at-risk" />
          <Stat label={`Recovered (${weeks}w)`} value={`${money(t.recovered, { compact: true })}/mo`} sub={`${t.recovered_count} payment${t.recovered_count === 1 ? "" : "s"}${t.avg_days_to_recover != null ? ` · avg ${t.avg_days_to_recover}d` : ""}`} tone="text-emerald-600" testid="recovery-stat-recovered" />
          <Stat label={`New failures (${weeks}w)`} value={`${money(t.new_failed, { compact: true })}/mo`} sub={`${t.new_failed_count} declined`} testid="recovery-stat-failed" />
          <Stat label="Trials converted" value={t.trials_converted} sub={`${money(t.trials_converted_mrr, { compact: true })}/mo won`} tone="text-blue-600" testid="recovery-stat-trials" />
          <Stat label="Outreach sent" value={t.chases + t.winbacks} sub={`${t.chases} chase · ${t.winbacks} win-back`} testid="recovery-stat-outreach" />
          <Stat label="Chase outcomes" value={t.outcomes?.chases ? `${t.outcomes.chases_paid}/${t.outcomes.chases} paid` : "—"} tone={t.outcomes?.chases_paid ? "text-emerald-700" : ""}
            sub={t.outcomes?.chases ? `${t.outcomes.chase_success_pct}% success${t.outcomes.avg_days_to_pay != null ? ` · avg ${t.outcomes.avg_days_to_pay}d to pay` : ""}${t.outcomes.auto_chases ? ` · ${t.outcomes.auto_chases} auto` : ""}` : "no chases yet"} testid="recovery-stat-chase-outcomes" />
          <Stat label="Win-back outcomes" value={t.outcomes?.winbacks ? `${t.outcomes.winbacks_converted}/${t.outcomes.winbacks} back` : "—"} tone={t.outcomes?.winbacks_converted ? "text-emerald-700" : ""}
            sub={t.outcomes?.winbacks ? `${t.outcomes.winback_success_pct}% picked a plan` : "no win-backs yet"} testid="recovery-stat-winback-outcomes" />
        </div>
      )}
      <div className="h-52">
        {error ? <div className="flex items-center justify-center h-full text-xs text-muted-foreground">Couldn't load recovery trend</div>
          : !data ? <div className="flex items-center justify-center h-full text-xs text-muted-foreground">Loading…</div>
          : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" tickFormatter={(v) => `£${v >= 1000 ? `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k` : v}`} />
                <Tooltip content={<TrendTip />} cursor={{ fill: "var(--muted)", opacity: 0.4 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="at_risk" name="MRR at risk" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="recovered" name="MRR recovered" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
      </div>
    </div>
  );
}
